import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Clock, Save, Loader2, RotateCcw, ChevronDown, ChevronUp, X } from "lucide-react";

const DAY_NAMES = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const TIME_OPTIONS = [];
for (let h = 7; h <= 22; h++) {
  TIME_OPTIONS.push(`${h.toString().padStart(2, "0")}:00`);
  TIME_OPTIONS.push(`${h.toString().padStart(2, "0")}:30`);
}

const DEFAULT_HOURS = DAY_NAMES.map((_, i) => ({
  day: i,
  start_time: "09:00",
  end_time: "19:00",
  is_working: i < 6,
  excluded_slots: [],
}));

// Generate all 30-min slots between start and end
function generateSlots(start, end) {
  const slots = [];
  const [sh, sm] = start.split(":").map(Number);
  const [eh, em] = end.split(":").map(Number);
  let current = sh * 60 + sm;
  const endMin = eh * 60 + em;
  while (current < endMin) {
    const hh = Math.floor(current / 60).toString().padStart(2, "0");
    const mm = (current % 60).toString().padStart(2, "0");
    slots.push(`${hh}:${mm}`);
    current += 30;
  }
  return slots;
}

export default function AdminWorkingHours() {
  const { token } = useAuth();
  const [barbers, setBarbers] = useState([]);
  const [selectedBarberId, setSelectedBarberId] = useState("");
  const [workingHours, setWorkingHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const response = await axios.get(`${API}/barbers`);
        setBarbers(response.data);
        if (response.data.length > 0) {
          setSelectedBarberId(response.data[0].id);
        }
      } catch (error) {
        toast.error("Berberler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchBarbers();
  }, []);

  useEffect(() => {
    if (selectedBarberId) {
      const barber = barbers.find(b => b.id === selectedBarberId);
      if (barber) {
        const hours = barber.working_hours && barber.working_hours.length > 0
          ? barber.working_hours
          : DEFAULT_HOURS;
        const fullHours = DAY_NAMES.map((_, i) => {
          const existing = hours.find(h => h.day === i);
          return existing
            ? { ...existing, excluded_slots: existing.excluded_slots || [] }
            : { day: i, start_time: "09:00", end_time: "19:00", is_working: false, excluded_slots: [] };
        });
        setWorkingHours(fullHours);
        setExpandedDay(null);
      }
    }
  }, [selectedBarberId, barbers]);

  const toggleDay = (dayIndex) => {
    setWorkingHours(prev =>
      prev.map(h => h.day === dayIndex ? { ...h, is_working: !h.is_working, excluded_slots: [] } : h)
    );
  };

  const updateTime = (dayIndex, field, value) => {
    setWorkingHours(prev =>
      prev.map(h => h.day === dayIndex ? { ...h, [field]: value, excluded_slots: [] } : h)
    );
  };

  const toggleSlotExclusion = (dayIndex, slot) => {
    setWorkingHours(prev =>
      prev.map(h => {
        if (h.day !== dayIndex) return h;
        const excluded = h.excluded_slots || [];
        const isExcluded = excluded.includes(slot);
        return {
          ...h,
          excluded_slots: isExcluded
            ? excluded.filter(s => s !== slot)
            : [...excluded, slot].sort()
        };
      })
    );
  };

  const applyToAll = (dayIndex) => {
    const source = workingHours.find(h => h.day === dayIndex);
    if (!source) return;
    setWorkingHours(prev =>
      prev.map(h => h.is_working ? { ...h, start_time: source.start_time, end_time: source.end_time, excluded_slots: [...(source.excluded_slots || [])] } : h)
    );
    toast.success("Saatler tüm açık günlere uygulandı");
  };

  const resetToDefault = () => {
    setWorkingHours(DEFAULT_HOURS.map(h => ({ ...h, excluded_slots: [] })));
    setExpandedDay(null);
    toast.info("Varsayılan saatlere sıfırlandı");
  };

  const handleSave = async () => {
    for (const h of workingHours) {
      if (h.is_working && h.start_time >= h.end_time) {
        toast.error(`${DAY_NAMES[h.day]}: Başlangıç saati bitiş saatinden önce olmalı`);
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/barbers/${selectedBarberId}`,
        { working_hours: workingHours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBarbers(prev =>
        prev.map(b => b.id === selectedBarberId ? { ...b, working_hours: workingHours } : b)
      );
      toast.success("Çalışma saatleri kaydedildi!");
    } catch (error) {
      toast.error("Çalışma saatleri kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Çalışma Saatleri">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Çalışma Saatleri">
      <div data-testid="admin-working-hours">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
          <div>
            <h2 className="font-display text-xl text-[#1a1a2e] mb-1">Çalışma Saatleri Yönetimi</h2>
            <p className="text-[#6b7280] text-sm">Her berber için günleri, saatleri ve kapalı zaman dilimlerini ayarlayın.</p>
          </div>

          <div className="flex gap-3">
            <Select value={selectedBarberId} onValueChange={setSelectedBarberId}>
              <SelectTrigger className="w-[200px] bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e]">
                <SelectValue placeholder="Berber Seçin" />
              </SelectTrigger>
              <SelectContent className="bg-white border-[#e5e7eb]">
                {barbers.map((barber) => (
                  <SelectItem key={barber.id} value={barber.id}>{barber.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {barbers.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-xl p-12 text-center shadow-sm">
            <Clock className="w-12 h-12 text-[#9ca3af] mx-auto mb-4" />
            <p className="text-[#6b7280]">Henüz berber eklenmemiş. Önce berber ekleyin.</p>
          </div>
        ) : (
          <>
            {/* Working Hours Cards */}
            <div className="space-y-3">
              {workingHours.map((h) => {
                const daySlots = h.is_working ? generateSlots(h.start_time, h.end_time) : [];
                const excludedCount = (h.excluded_slots || []).length;
                const isExpanded = expandedDay === h.day;

                return (
                  <div
                    key={h.day}
                    className={`bg-white border rounded-xl shadow-sm overflow-hidden transition-colors ${
                      h.is_working ? "border-[#e5e7eb]" : "border-[#f3f4f6] opacity-60"
                    }`}
                  >
                    {/* Day Row */}
                    <div className="flex flex-col md:flex-row md:items-center gap-3 p-4">
                      {/* Day Name + Toggle */}
                      <div className="flex items-center gap-4 min-w-[180px]">
                        <button
                          onClick={() => toggleDay(h.day)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            h.is_working ? "bg-emerald-500" : "bg-[#d1d5db]"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              h.is_working ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                        <span className={`font-medium text-sm ${h.is_working ? "text-[#1a1a2e]" : "text-[#9ca3af]"}`}>
                          {DAY_NAMES[h.day]}
                        </span>
                      </div>

                      {/* Time Selectors */}
                      {h.is_working && (
                        <>
                          <div className="flex items-center gap-2 flex-1">
                            <Select
                              value={h.start_time}
                              onValueChange={(v) => updateTime(h.day, "start_time", v)}
                            >
                              <SelectTrigger className="w-[100px] bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e] text-sm h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-[#e5e7eb] max-h-[200px]">
                                {TIME_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-[#9ca3af] text-sm">—</span>
                            <Select
                              value={h.end_time}
                              onValueChange={(v) => updateTime(h.day, "end_time", v)}
                            >
                              <SelectTrigger className="w-[100px] bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e] text-sm h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-white border-[#e5e7eb] max-h-[200px]">
                                {TIME_OPTIONS.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Excluded count badge + expand button */}
                          <div className="flex items-center gap-3">
                            {excludedCount > 0 && (
                              <span className="text-xs bg-red-50 text-red-600 px-2 py-1 rounded-full font-medium">
                                {excludedCount} saat kapalı
                              </span>
                            )}
                            <button
                              onClick={() => setExpandedDay(isExpanded ? null : h.day)}
                              className="flex items-center gap-1 text-xs text-[#c9a96e] hover:text-[#b8944f] transition-colors font-medium"
                            >
                              Saatleri Düzenle
                              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            </button>
                            <button
                              onClick={() => applyToAll(h.day)}
                              className="text-xs text-[#6b7280] hover:text-[#1a1a2e] transition-colors"
                              title="Bu saatleri tüm açık günlere uygula"
                            >
                              Tümüne Uygula
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Expanded: Time Slot Grid */}
                    {h.is_working && isExpanded && (
                      <div className="border-t border-[#f3f4f6] bg-[#fafafa] p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs text-[#6b7280]">
                            Kapatmak istediğiniz saatlere tıklayın. <span className="text-red-500">Kırmızı</span> = kapalı
                          </p>
                          {excludedCount > 0 && (
                            <button
                              onClick={() => setWorkingHours(prev =>
                                prev.map(wh => wh.day === h.day ? { ...wh, excluded_slots: [] } : wh)
                              )}
                              className="text-xs text-[#6b7280] hover:text-[#1a1a2e] flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              Tümünü Aç
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-1.5">
                          {daySlots.map((slot) => {
                            const isExcluded = (h.excluded_slots || []).includes(slot);
                            return (
                              <button
                                key={slot}
                                onClick={() => toggleSlotExclusion(h.day, slot)}
                                className={`py-2 px-1 text-xs font-medium rounded-lg border transition-all ${
                                  isExcluded
                                    ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                                    : "bg-white border-[#e5e7eb] text-[#1a1a2e] hover:border-[#c9a96e] hover:bg-[#c9a96e]/5"
                                }`}
                              >
                                {slot}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center mt-6">
              <Button
                onClick={resetToDefault}
                variant="ghost"
                className="text-[#6b7280] hover:text-[#1a1a2e]"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Varsayılana Sıfırla
              </Button>

              <Button
                onClick={handleSave}
                className="btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>

            {/* Info */}
            <div className="mt-6 bg-[#c9a96e]/10 border border-[#c9a96e]/30 rounded-lg p-4">
              <p className="text-[#c9a96e] text-sm">
                <strong>Not:</strong> Çalışma saatleri berber bazında ayarlanır. "Saatleri Düzenle" ile gün içinde belirli saatleri kapatabilirsiniz. Müşteriler randevu alırken sadece açık olan günleri ve saatleri görecektir.
              </p>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
